import { MeurhArquivosTrabalhadorService } from '../arquivos.trabalhador.service';
import { MeurhVisualizarArquivoModalService } from '../../modal/visualizararquivo/visualizararquivo.modal.service';
import { MeurhArquivosTrabalhadorUploadModalService } from '../modals/upload/arquivos.trabalhador.upload.modal.service';
import {
    IArquivoTrabalhador,
    IArquivoTrabalhadorUploadPayload,
} from '../../model/arquivos.trabalhador.service.model';
import {
    IArquivoTrabalhadorUploadSubmitData,
} from '../modals/upload/arquivos.trabalhador.upload.modal.model';
import { TipoArquivosService } from '../../../TipoArquivos/tipo-arquivos.service';
import { ITipoArquivoResponse } from '../../../TipoArquivos/models/tipo-arquivos.service.model';
import { resolverNomeArquivoDownload } from '@utils/utils';
import { ModalConfirmService } from '../../../../shared/components/modal-confirm/modal.confirm.service';

type IArquivoTrabalhadorViewModel = IArquivoTrabalhador & {
    selecionado?: boolean;
    situacaoAtualLabel: string;
    situacaoClasse: string;
};

export class MeurhArquivosTrabalhadorIndexController {
    static $inject = [
        'MeurhArquivosTrabalhadorService',
        'MeurhVisualizarArquivoModalService',
        'MeurhArquivosTrabalhadorUploadModalService',
        'TipoArquivosService',
        'ModalConfirmService',
        'NewToaster',
    ];

    public colaborador: string;

    public pastas: ITipoArquivoResponse[] = [];
    public pastaAtual: ITipoArquivoResponse;
    public arquivos: IArquivoTrabalhadorViewModel[] = [];
    public busy = false;
    public loading = {
        pastas: false,
        arquivos: false
    };
    private readonly situacaoLabelToClassMap: Record<string, string> = {
        'Pendente de liberação': 'label-warning',
        'Pendente de assinatura': 'label-rascunho',
        Assinado: 'label-success',
        Entregue: 'label-success',
        Cancelado: 'label-default',
    };

    constructor(
        public MeurhArquivosTrabalhadorService: MeurhArquivosTrabalhadorService,
        public MeurhVisualizarArquivoModalService: MeurhVisualizarArquivoModalService,
        public MeurhArquivosTrabalhadorUploadModalService: MeurhArquivosTrabalhadorUploadModalService,
        public TipoArquivosService: TipoArquivosService,
        public ModalConfirmService: ModalConfirmService,
        public NewToaster: { pop: Function },
    ) {
        this.getPastas();
    }

    getPastas() {
        this.loading.pastas = true;
        this.TipoArquivosService.getList().then((result) => {
            this.pastas = result;
        }).finally(() => this.loading.pastas = false);
    }

    getArquivos(pasta: ITipoArquivoResponse) {
        this.pastaAtual = pasta;
        this.arquivos = [];
        this.loading.arquivos = true;
        this.MeurhArquivosTrabalhadorService.getArquivos(pasta.tipoarquivo, this.colaborador)
            .then((result) => {
                this.arquivos = result.map((arquivo) => this.decorateArquivo(arquivo));
            }).finally(() => this.loading.arquivos = false);
    }

    visualizarArquivo(id: string, extensao: string) {
        const modal = this.MeurhVisualizarArquivoModalService.open(
            id,
            extensao,
            this.MeurhArquivosTrabalhadorService
        );
        modal.result
            .then(() => {/* */})
            .catch((error) => {
                console.log(error);
            });
    }

    downloadArquivos(arquivo: IArquivoTrabalhador) {
        const arquivosSelecionados = arquivo ? [arquivo] : this.arquivos.filter(arquivo => arquivo.selecionado);
        this.busy = true;
        if (arquivosSelecionados.length === 1) {
            this.MeurhArquivosTrabalhadorService.getConteudo(arquivosSelecionados[0].arquivotrabalhador)
            .then((response) => {
                const nomeArquivo = this.resolverNomeArquivoDownload(response, arquivosSelecionados[0]);
                this.handleDownload(response.data, nomeArquivo, 'application/octet-stream');
            }).finally(() => this.busy = false);
        } else {
            const param = (!arquivosSelecionados.length ? this.arquivos : arquivosSelecionados)
            .map((arq) => ({ arquivotrabalhador: arq.arquivotrabalhador }));

            this.MeurhArquivosTrabalhadorService.downloadArquivos(param)
            .then((result) => {
                this.handleDownload(result, 'arquivos.zip');
            }).finally(() => this.busy = false);
        }
    }

    podeLiberarArquivo(arquivo: IArquivoTrabalhador) {
        return !!arquivo?.arquivotrabalhador && arquivo.situacaoliberacao === false && !this.busy;
    }

    abrirConfirmacaoLiberarArquivo(arquivo: IArquivoTrabalhador) {
        if (!this.podeLiberarArquivo(arquivo)) {
            return;
        }

        const textos = {
            title: 'Liberar arquivo',
            subtitle: arquivo.nome ? `Você está prestes a liberar ${arquivo.nome}.` : '',
            confirmText: 'Tem certeza que deseja liberar este arquivo para o colaborador?',
            closeButton: 'Cancelar',
            confirmButton: 'Sim, liberar',
        };

        const modal = this.ModalConfirmService.open(arquivo, 'arquivo', false, textos, true);
        modal.result
            .then(() => {
                this.liberarArquivo(arquivo);
            })
            .catch(() => undefined);
    }

    canUploadArquivo() {
        return !!this.pastaAtual && !this.pastaAtual.tipopadrao && !this.loading.arquivos && !this.busy;
    }

    showUploadBlockedTooltip() {
        return !!this.pastaAtual?.tipopadrao;
    }

    abrirUploadArquivo() {
        if (!this.canUploadArquivo()) {
            return;
        }

        const modal = this.MeurhArquivosTrabalhadorUploadModalService.open();
        modal.result
            .then((payload: IArquivoTrabalhadorUploadSubmitData) => {
                this.realizarUploadArquivo(payload);
            })
            .catch(() => undefined);
    }

    private liberarArquivo(arquivo: IArquivoTrabalhador) {
        if (!this.podeLiberarArquivo(arquivo)) {
            return;
        }

        this.busy = true;
        this.MeurhArquivosTrabalhadorService.liberarArquivo(arquivo.arquivotrabalhador)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Arquivo liberado com sucesso!',
                });

                if (this.pastaAtual) {
                    this.getArquivos(this.pastaAtual);
                }
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao liberar arquivo',
                    body: error?.data?.message || 'Ocorreu um erro ao tentar liberar o arquivo.',
                });
            })
            .finally(() => {
                this.busy = false;
            });
    }

    private handleDownload(file: File, name: string, type?: string) {
        const blob = new Blob([file], { type });
        const fileURL = URL.createObjectURL(blob);
        const a = document.createElement('a');

        document.body.appendChild(a);
        a.style.display = 'none';
        a.href = fileURL;
        a.download = name;
        a.click();
        window.URL.revokeObjectURL(fileURL);
    }

    private resolverNomeArquivoDownload(response: angular.IHttpResponse<File>, arquivo: IArquivoTrabalhador): string {
        return resolverNomeArquivoDownload(
            response.headers('Content-Disposition'),
            arquivo.nome,
            arquivo.extensao
        );
    }

    private realizarUploadArquivo(payload: IArquivoTrabalhadorUploadSubmitData) {
        if (!this.pastaAtual?.codigo || !this.colaborador) {
            return;
        }

        const uploadPayload: IArquivoTrabalhadorUploadPayload = {
            ...payload,
            trabalhador: this.colaborador,
            tipoarquivo_codigo: this.pastaAtual.codigo,
        };

        this.busy = true;
        this.MeurhArquivosTrabalhadorService.uploadArquivo(uploadPayload)
            .then(() => {
                this.NewToaster.pop({
                    type: 'success',
                    title: 'Arquivo enviado com sucesso!',
                });

                this.getArquivos(this.pastaAtual);
            })
            .catch((error) => {
                this.NewToaster.pop({
                    type: 'error',
                    title: 'Erro ao enviar arquivo',
                    body: error?.data?.message || 'Ocorreu um erro ao enviar o arquivo.',
                });
            })
            .finally(() => {
                this.busy = false;
            });
    }

    private decorateArquivo(arquivo: IArquivoTrabalhador): IArquivoTrabalhadorViewModel {
        const situacaoAtualLabel = this.resolveSituacaoAtualLabel(arquivo);

        return {
            ...arquivo,
            situacaoAtualLabel,
            situacaoClasse: this.getSituacaoClasse(situacaoAtualLabel),
        };
    }

    private resolveSituacaoAtualLabel(arquivo: IArquivoTrabalhador): string {
        if (arquivo.situacaoassinatura === 4) {
            return 'Cancelado';
        }

        if (arquivo.situacaoliberacao === false) {
            return 'Pendente de liberação';
        }

        if (arquivo.situacaoassinatura === 2) {
            return 'Assinado';
        }

        if (arquivo.situacaoassinatura === 1) {
            return 'Pendente de assinatura';
        }

        if (arquivo.situacaoliberacao === true) {
            return 'Entregue';
        }

        return '';
    }

    private getSituacaoClasse(situacao: string): string {
        return this.situacaoLabelToClassMap[situacao] || 'label-default';
    }
}
